// Athlete Matchup Comparison — pure helpers (no React, no JSON imports, so it
// runs under Node's test runner; callers inject the site's existing data:
// athletes.generated.json profiles, EVENT_ARCHIVE, RANKINGS_OFFICIAL, MATCH_CARD).
//
// It only ever surfaces fields the data already has. It never invents stats and
// never predicts a winner. Unit tests: tests/matchup.test.ts.
import { normName, looseName } from './names.ts';

export interface Measure { imp: string | null; cm?: string | null; kg?: string | null; lbs?: string | null }
export interface Profile {
  slug: string;
  name: string;
  nickname: string | null;
  country: string | null;
  age: number | null;
  wins: number | null;
  losses: number | null;
  height: Measure;
  weight: Measure;
  biceps: Measure;
  forearm: Measure;
  wrist: Measure;
  bio?: string[];
}
export interface Bout { winner: string; loser: string; score: string; arm: 'left' | 'right'; weightClass: string }
export interface EventRec { num: number; slug: string; name: string; isoDate: string; matches: Bout[] }
export interface RankRow { rank: number; name: string; champion?: boolean; vacant?: boolean }
export interface WeightClass { className: string; arms: { right?: RankRow[]; left?: RankRow[] } }

/** Unique profiles (the manifest keys each profile by several spellings), A–Z. */
export function uniqueProfiles(manifest: Record<string, Profile>): Profile[] {
  const seen = new Map<string, Profile>();
  for (const p of Object.values(manifest)) if (!seen.has(p.slug)) seen.set(p.slug, p);
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/** Resolve a name as written in the archive/rankings to a profile (exact, then loose). */
export function profileForName(manifest: Record<string, Profile>, name: string): Profile | null {
  return manifest[normName(name)] || manifest[looseName(name)] || null;
}

// Letters NFD can't decompose (ł, ø, đ, ß …) folded for search only — the
// shared normName must stay byte-identical to the scrapers' key function.
const FOLD: Record<string, string> = { ł: 'l', Ł: 'l', ø: 'o', Ø: 'o', đ: 'd', Đ: 'd', ß: 'ss', æ: 'ae', œ: 'oe', ı: 'i' };
const searchKey = (s: string) => normName(s.replace(/[łŁøØđĐßæœı]/g, (c) => FOLD[c] || c));

/** Accent-insensitive search over name + nickname + country; prefix matches first. */
export function searchProfiles(list: Profile[], query: string, limit = 8): Profile[] {
  const q = searchKey(query);
  if (!q) return [];
  const scored: { p: Profile; s: number }[] = [];
  for (const p of list) {
    const n = searchKey(p.name);
    const hay = `${n} ${searchKey(p.nickname || '')} ${searchKey(p.country || '')}`;
    let s = -1;
    if (n.startsWith(q)) s = 0;
    else if (n.split(' ').some((w) => w.startsWith(q))) s = 1;
    else if (hay.includes(q)) s = 2;
    if (s >= 0) scored.push({ p, s });
  }
  return scored.sort((a, b) => a.s - b.s || a.p.name.localeCompare(b.p.name, 'en')).slice(0, limit).map((x) => x.p);
}

// ---------------------------------------------------------------------------
// Pair slugs: "<a>-vs-<b>" with the two slugs sorted, so A-vs-B and B-vs-A
// share one canonical URL.
// ---------------------------------------------------------------------------

export function pairSlug(a: string, b: string): string {
  const [x, y] = [a, b].sort();
  return `${x}-vs-${y}`;
}

export function parsePairSlug(pair: string, known: Set<string>): [string, string] | null {
  // Slugs contain hyphens, so try every "-vs-" split point.
  let idx = pair.indexOf('-vs-');
  while (idx !== -1) {
    const a = pair.slice(0, idx);
    const b = pair.slice(idx + 4);
    if (known.has(a) && known.has(b) && a !== b) return [a, b];
    idx = pair.indexOf('-vs-', idx + 1);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Derived, factual comparison data
// ---------------------------------------------------------------------------

/**
 * Does `name` (as written in the archive/rankings) refer to this profile?
 * Exact normalised name first, then the manifest's alias keys — needed because
 * the archive writes e.g. "Kamil Jablonski" while the profile says "Jabłoński"
 * (NFD can't decompose "ł", so plain normName comparison misses it).
 */
export function isPerson(name: string, p: Profile, manifest: Record<string, Profile>): boolean {
  if (!name) return false;
  if (normName(name) === normName(p.name)) return true;
  return profileForName(manifest, name)?.slug === p.slug;
}

export interface AthleteBout {
  eventSlug: string; eventName: string; num: number; isoDate: string;
  opponent: string; won: boolean; score: string; arm: 'left' | 'right'; weightClass: string;
}

export function boutsFor(p: Profile, archive: EventRec[], manifest: Record<string, Profile>): AthleteBout[] {
  const out: AthleteBout[] = [];
  for (const ev of archive) {
    for (const m of ev.matches) {
      const won = isPerson(m.winner, p, manifest);
      if (!won && !isPerson(m.loser, p, manifest)) continue;
      out.push({
        eventSlug: ev.slug, eventName: ev.name, num: ev.num, isoDate: ev.isoDate,
        opponent: won ? m.loser : m.winner, won, score: m.score, arm: m.arm, weightClass: m.weightClass,
      });
    }
  }
  return out.sort((a, b) => b.num - a.num);
}

export interface RankSlot { className: string; arm: 'right' | 'left'; rank: number; champion: boolean }
export function rankSlotsFor(p: Profile, rankings: WeightClass[], manifest: Record<string, Profile>): RankSlot[] {
  const out: RankSlot[] = [];
  for (const wc of rankings) {
    for (const arm of ['right', 'left'] as const) {
      for (const r of wc.arms[arm] || []) {
        if (!r.vacant && r.name && isPerson(r.name, p, manifest)) out.push({ className: wc.className, arm, rank: r.rank, champion: !!r.champion });
      }
    }
  }
  return out;
}

export interface SideSummary {
  profile: Profile;
  evwBouts: number;
  evwWins: number;
  evwLosses: number;
  rightArmBouts: number;
  leftArmBouts: number;
  evwEvents: number;
  lastEvent: { slug: string; name: string; isoDate: string } | null;
  weightClasses: string[];
  rankings: RankSlot[];
  bestRank: number | null;
}

export function summarize(profile: Profile, archive: EventRec[], rankings: WeightClass[], manifest: Record<string, Profile>): SideSummary {
  const bouts = boutsFor(profile, archive, manifest);
  const ranks = rankSlotsFor(profile, rankings, manifest);
  const events = new Set(bouts.map((b) => b.eventSlug));
  const classes = [...new Set(bouts.map((b) => b.weightClass).filter(Boolean))];
  return {
    profile,
    evwBouts: bouts.length,
    evwWins: bouts.filter((b) => b.won).length,
    evwLosses: bouts.filter((b) => !b.won).length,
    rightArmBouts: bouts.filter((b) => b.arm === 'right').length,
    leftArmBouts: bouts.filter((b) => b.arm === 'left').length,
    evwEvents: events.size,
    lastEvent: bouts[0] ? { slug: bouts[0].eventSlug, name: bouts[0].eventName, isoDate: bouts[0].isoDate } : null,
    weightClasses: classes,
    rankings: ranks,
    bestRank: ranks.length ? Math.min(...ranks.map((r) => r.rank)) : null,
  };
}

export interface HeadToHeadBout { eventSlug: string; eventName: string; isoDate: string; winner: string; loser: string; score: string; arm: 'left' | 'right'; weightClass: string; aWon: boolean }

/** Every EVW bout between the two, newest first, with names as the archive wrote them. */
export function headToHead(a: Profile, b: Profile, archive: EventRec[], manifest: Record<string, Profile>): HeadToHeadBout[] {
  const out: (HeadToHeadBout & { num: number })[] = [];
  for (const ev of archive) {
    for (const m of ev.matches) {
      const aWon = isPerson(m.winner, a, manifest) && isPerson(m.loser, b, manifest);
      const bWon = isPerson(m.winner, b, manifest) && isPerson(m.loser, a, manifest);
      if (aWon || bWon) out.push({ num: ev.num, eventSlug: ev.slug, eventName: ev.name, isoDate: ev.isoDate, ...m, aWon });
    }
  }
  out.sort((x, y) => y.num - x.num);
  return out.map((x) => ({
    eventSlug: x.eventSlug, eventName: x.eventName, isoDate: x.isoDate, winner: x.winner, loser: x.loser,
    score: x.score, arm: x.arm, weightClass: x.weightClass, aWon: x.aWon,
  }));
}

/** Events both athletes pulled at (against anyone), newest first. */
export function sharedEvents(a: Profile, b: Profile, archive: EventRec[], manifest: Record<string, Profile>): { slug: string; name: string; isoDate: string }[] {
  const has = (ev: EventRec, p: Profile) => ev.matches.some((m) => isPerson(m.winner, p, manifest) || isPerson(m.loser, p, manifest));
  return archive
    .filter((ev) => has(ev, a) && has(ev, b))
    .sort((x, y) => y.num - x.num)
    .map((ev) => ({ slug: ev.slug, name: ev.name, isoDate: ev.isoDate }));
}

/** Common opponents (faced by both in EVW), with each side's results against them. */
export function commonOpponents(a: Profile, b: Profile, archive: EventRec[], manifest: Record<string, Profile>): { opponent: string; a: boolean[]; b: boolean[] }[] {
  const key = (name: string) => profileForName(manifest, name)?.slug || normName(name);
  const map = new Map<string, { opponent: string; a: boolean[]; b: boolean[] }>();
  for (const x of boutsFor(a, archive, manifest)) {
    if (isPerson(x.opponent, b, manifest)) continue;
    const k = key(x.opponent);
    if (!map.has(k)) map.set(k, { opponent: x.opponent, a: [], b: [] });
    map.get(k)!.a.push(x.won);
  }
  for (const y of boutsFor(b, archive, manifest)) {
    const k = key(y.opponent);
    if (isPerson(y.opponent, a, manifest) || !map.has(k)) continue;
    map.get(k)!.b.push(y.won);
  }
  return [...map.values()].filter((r) => r.a.length && r.b.length).sort((x, y) => x.opponent.localeCompare(y.opponent, 'en'));
}

// ---------------------------------------------------------------------------
// Popular matchups to pre-render (SEO): the upcoming card, the top of every
// official ranking ladder, and repeat EVW rivalries. Only pairs where both
// athletes have an on-site profile.
// ---------------------------------------------------------------------------

export function popularPairs(opts: {
  manifest: Record<string, Profile>;
  rankings: WeightClass[];
  archive: EventRec[];
  card?: { a: { name: string }; b: { name: string } }[];
  limit?: number;
}): [string, string][] {
  const { manifest, rankings, archive, card = [], limit = 24 } = opts;
  const out: [string, string][] = [];
  const seen = new Set<string>();
  const add = (x: string, y: string) => {
    const pa = profileForName(manifest, x);
    const pb = profileForName(manifest, y);
    if (!pa || !pb || pa.slug === pb.slug) return;
    const key = pairSlug(pa.slug, pb.slug);
    if (seen.has(key)) return;
    seen.add(key);
    out.push([pa.slug, pb.slug].sort() as [string, string]);
  };
  for (const m of card) add(m.a.name, m.b.name);
  // Ranking ladders: the top two non-vacant names per class and arm.
  for (const wc of rankings) {
    for (const arm of ['right', 'left'] as const) {
      const top = (wc.arms[arm] || []).filter((r) => !r.vacant && r.name).sort((p, q) => p.rank - q.rank).slice(0, 2);
      if (top.length === 2) add(top[0].name, top[1].name);
    }
  }
  // Rivalries: pairs that met more than once, most meetings first.
  const counts = new Map<string, { a: string; b: string; n: number; last: number }>();
  for (const ev of archive) {
    for (const m of ev.matches) {
      const k = [normName(m.winner), normName(m.loser)].sort().join('|');
      const c = counts.get(k) || { a: m.winner, b: m.loser, n: 0, last: 0 };
      c.n++;
      c.last = Math.max(c.last, ev.num);
      counts.set(k, c);
    }
  }
  [...counts.values()]
    .filter((c) => c.n >= 2)
    .sort((p, q) => q.n - p.n || q.last - p.last)
    .forEach((c) => add(c.a, c.b));
  return out.slice(0, limit);
}
